import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import {
  BackArrowIcon,
  getKnowledgeBaseIcon,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import {
  editEmptyFolder,
  encodePathSegment,
  getChildrenFiles,
  getParentFolder,
  getRootFolder,
  removeEmptyFolders,
  setCheckedChildren,
  type TreeNode,
  updateBranchInTree,
} from 'src/utils/fileUtils';
import {
  useUpdateKnowledgeBaseFileMutation,
  useUpdateKnowledgeBaseFilesMutation,
} from 'src/services/knowledgeBaseService';
import { CurrentKnowledgeBaseTableRow } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/CurrentKnowledgeBaseTableRow/CurrentKnowledgeBaseTableRow';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { useAppSelector } from 'src/store';
import { FILE_STATE } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import type {
  ItemDeleteHandler,
  ItemEditHandler,
  ItemMoveHandler,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/knowledgeBaseItemHandlers';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const getEmptyBranchMessage = (branch: TreeNode) => {
  if (branch.root) return 'No data sources yet';

  const label = branch.type ? branch.type[0].toUpperCase() + branch.type.slice(1) : 'Folder';
  return `${label} is empty`;
};

export const CurrentKnowledgeBaseTableList = ({
  onCheckedChange,
  isLoadingFiles,
  deleteContentMode,
  moveContentMode,
  handleItemsDelete,
  handleMoveItems,
}: {
  onCheckedChange: () => void;
  isLoadingFiles: boolean;
  deleteContentMode: boolean;
  moveContentMode: boolean;
  handleItemsDelete: (files?: TreeNode[]) => Promise<void>;
  handleMoveItems: (moveToFolder: TreeNode) => Promise<void>;
}) => {
  const [isEmpty, setIsEmpty] = useState(false);
  const { id } = useParams();
  const notification = useNotification();
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  const { setIsEditPopupOpen, setSelectedItem, activeBranch, updateActiveBranch } = useKBContext();
  const parentFolder = activeBranch && getParentFolder(activeBranch);

  const [updateKnowledgeBaseFiles, { isLoading: isLoadingUpdateKnowledgeBaseFiles }] =
    useUpdateKnowledgeBaseFilesMutation();

  const [updateKnowledgeBaseFile, { isLoading: isLoadingUpdateKnowledgeBaseFile }] =
    useUpdateKnowledgeBaseFileMutation();

  useLoaderWhile(isLoadingUpdateKnowledgeBaseFiles, isLoadingUpdateKnowledgeBaseFile);

  useEffect(() => {
    if (activeBranch) {
      setIsEmpty(!activeBranch.children.some((child) => child.visible));
    }
  }, [activeBranch]);

  const handleRowClick = (item: TreeNode) => {
    const is = {
      file: item.type === 'file',
      folder: item.type === 'folder',
      md: item.name.endsWith('.md'),
      txt: item.name.endsWith('.txt'),
      faq: item.type === 'FAQ',
      integration: item.type === 'integration' && !item.collector_id,
      zendeskTickets: Boolean(item?.data?.type === 'zendesk_tickets'),
      freshdeskTickets: Boolean(item?.data?.type === 'freshdesk_tickets'),
      website: item.type === 'website' && !item.collector_id,
      websiteChild: item.type === 'website' && Boolean(item.collector_id),
      selection: Boolean(globalThis.getSelection().toString().length),
    };

    if (is.selection) {
      return;
    }

    setSelectedItem(item);

    if (!is.folder && (is.md || is.faq || is.txt || is.websiteChild)) {
      if (![FILE_STATE.DELETING, FILE_STATE.UPLOADING].includes(item.state)) {
        setIsEditPopupOpen(true);
        return;
      }
    }

    if (is.zendeskTickets || is.freshdeskTickets) {
      return;
    }

    if (is.folder || is.integration || is.website) {
      updateActiveBranch(item);
    }
  };

  const handleSingleItemEdit: ItemEditHandler = async (name, item) => {
    let files = [item];

    if (item.type === 'folder') {
      files = getChildrenFiles(item);

      if (!files.length) {
        const newActiveBranch = {
          ...activeBranch,
          children: activeBranch.children.filter((folder) => folder.path !== item.path),
        };

        editEmptyFolder(name, item);
        updateActiveBranch(newActiveBranch);
        notification.success('Updated Successfully.');
        return;
      }
    }

    const request = files.map((file) => {
      if (item.type === 'file' || item.type === 'FAQ') {
        return {
          id: file.id,
          name: item.type === 'FAQ' ? `${name}.faq` : name,
          path: getParentFolder(file)?.path,
        };
      }
      if (item.type === 'folder') {
        const parentPath = getParentFolder(item)?.path ?? '/';
        const newPath = `${parentPath}/${encodePathSegment(name)}`.replace(/\/\//, '/');

        // Swap the renamed folder's prefix in the file's folder path; the rest stays as is.
        return {
          id: file.id,
          name: file.name,
          path: `${newPath}${getParentFolder(file).path.slice(item.path.length)}`,
        };
      }
    });

    if (request.length === 1) {
      const updateKnowledgeBaseFileResult = await updateKnowledgeBaseFile({
        knowledgeBaseId: id,
        fileId: request[0].id,
        body: {
          name: request[0].name,
          path: request[0].path,
        },
      });
      if (!('error' in updateKnowledgeBaseFileResult)) {
        notification.success('Updated Successfully.');
      }
    }

    if (request.length > 1) {
      const updateKnowledgeBaseFilesResult = await updateKnowledgeBaseFiles({
        id: id,
        body: request,
      });
      if (!('error' in updateKnowledgeBaseFilesResult)) {
        notification.success('Updated Successfully.');
      }
    }
  };

  const handleSingleItemMove: ItemMoveHandler = async (moveToFolder, item) => {
    item.checked = true;
    if (item.type === 'folder') {
      setCheckedChildren(item);
    }

    await handleMoveItems(moveToFolder);
  };

  const handleSingleItemDelete: ItemDeleteHandler = async (item) => {
    if (deleteContentMode) {
      await handleItemsDelete();

      return;
    }

    const tree = getRootFolder(item);

    if (item.type === 'folder') {
      const files = getChildrenFiles(item);

      if (files.length) {
        await handleItemsDelete([item, ...files]);
      } else {
        const newActiveBranch = { ...activeBranch };

        newActiveBranch.children = newActiveBranch.children.filter((child) => child !== item);
        removeEmptyFolders([item]);
        updateBranchInTree(tree, activeBranch, newActiveBranch);
        updateActiveBranch(newActiveBranch);
      }
    } else {
      await handleItemsDelete([item]);
    }
  };

  return (
    <Table
      wrapperClassName="max-h-[71vh] py-0"
      className={'relative !border-separate !border-spacing-y-[1px]'}
    >
      <TableHeader className={'bg-white z-10'}>
        <TableRow>
          <TableHead className="text-center text-grey_text"></TableHead>
          <TableHead className="text-start text-grey_text">Name</TableHead>
          <TableHead className="text-center text-grey_text">Type</TableHead>
          <TableHead className="text-center text-grey_text">Updated</TableHead>
          {!isTrialPlan && <TableHead className="text-center text-grey_text">Auto-sync</TableHead>}
          <TableHead className="text-grey_text"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className="">
        {Boolean(parentFolder) && (
          <TableRow
            className={
              'transition cursor-pointer hover:shadow-[0px_1px_2px_0px_rgba(0,_0,_0,_0.25)] bg-[#F4F8F7] hover:bg-[#E2EAE8]'
            }
            onClick={() => handleRowClick(parentFolder)}
          >
            <TableCell
              padding={'pl-5 pr-0'}
              className="table-fixedt text-center min-w-[50px] max-w-[100px] border-[#E3E6E8] border-l !rounded-tl-none"
            >
              <div className="whitespace-nowrap hyphens-auto truncate flex items-center justify-center gap-2.5">
                <div>{getKnowledgeBaseIcon(parentFolder)}</div>
                <div className={'min-w-6'}></div>
              </div>
            </TableCell>
            <TableCell
              colSpan={5}
              padding={'p-5'}
              className="table-fixedt text-start max-w-80 border-[#E3E6E8] !rounded-tr-none"
            >
              <div className="whitespace-nowrap hyphens-auto truncate flex items-center justify-start">
                {<BackArrowIcon />}
              </div>
            </TableCell>
          </TableRow>
        )}
        {isEmpty && (
          <TableRow>
            <TableCell colSpan={6} className={'!rounded-t-none'}>
              <LoaderWrapper
                loader={<Skeleton className="w-full h-6" />}
                isLoading={isLoadingFiles}
              >
                <p className="opacity-70 font-semibold text-base text-ebony text-center">
                  {getEmptyBranchMessage(activeBranch)}
                </p>
              </LoaderWrapper>
            </TableCell>
          </TableRow>
        )}
        {!isEmpty &&
          activeBranch.children.map((item, index) => {
            return (
              <CurrentKnowledgeBaseTableRow
                key={`item-name-${item.name}-${item.id}-${index}`}
                item={item}
                handleRowClick={handleRowClick}
                handleSingleItemEdit={handleSingleItemEdit}
                handleSingleItemMove={handleSingleItemMove}
                handleSingleItemDelete={handleSingleItemDelete}
                onCheckedChange={onCheckedChange}
                deleteContentMode={deleteContentMode}
                moveContentMode={moveContentMode}
              />
            );
          })}
      </TableBody>
    </Table>
  );
};
